import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LineChart, MousePointerClick, Eye, Target, Search, BarChart3, AlertTriangle, ShieldCheck, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function GscAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/seo/gsc-analytics`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Google Search Console data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-zinc-500 text-xs">Connecting to Google Servers...</p>
      </div>
    );
  }

  // Setup Required State
  if (data?.status === "setup_required") {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <LineChart className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            Search Console Analytics
          </h3>
          <p className="text-sm text-gray-500 dark:text-[#94A3B8]">
            View live search traffic, clicks, and ranking data directly from Google.
          </p>
        </div>

        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] overflow-hidden relative shadow-xs">
          <div className="absolute top-0 left-1/4 w-[300px] h-[300px] bg-emerald-500/5 rounded-full blur-[100px] pointer-events-none z-0" />
          <CardContent className="pt-10 pb-12 px-6 flex flex-col items-center text-center relative z-10">
            <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-950/40 rounded-full flex items-center justify-center mb-6 border border-emerald-200 dark:border-emerald-800/60">
              <ShieldCheck className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white font-outfit mb-3">
              Connect to Google Search Console
            </h2>
            <p className="text-gray-500 dark:text-[#94A3B8] max-w-lg mb-8 leading-relaxed">
              To securely pull your live search data, you need to provide a Google Cloud Service Account JSON file. This ensures Jamadhan can read your analytics without exposing your personal Google password.
            </p>
            
            <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl p-6 text-left w-full max-w-2xl space-y-4">
              <h4 className="text-gray-900 dark:text-white font-semibold text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" /> Setup Instructions
              </h4>
              <ol className="list-decimal pl-5 space-y-2 text-gray-500 dark:text-[#94A3B8] text-sm">
                <li>Go to the <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">Google Cloud Console</a>.</li>
                <li>Enable the <strong>Google Search Console API</strong> for your project.</li>
                <li>Create a <strong>Service Account</strong> and generate a JSON Key.</li>
                <li>Go to your <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">Search Console Dashboard</a>.</li>
                <li>Add the Service Account email address as a <strong>Restricted User</strong> to your property.</li>
                <li>Rename the JSON file to <code>service-account.json</code> and upload it to the root of your backend server.</li>
              </ol>
            </div>
            
            <div className="mt-8 flex gap-4">
              <Button onClick={fetchAnalytics} className="bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white hover:bg-white/10 border border-gray-200 dark:border-white/10">
                <RefreshCw className="w-4 h-4 mr-2" /> Check Connection
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Analytics Data State
  const t = data?.totals || { clicks: 0, impressions: 0, ctr: 0, position: 0 };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <LineChart className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            Google Search Performance
          </h3>
          <p className="text-sm text-gray-500 dark:text-[#94A3B8]">
            Last 30 Days (Data directly from Google Search Console)
          </p>
        </div>
        <Button onClick={fetchAnalytics} variant="outline" size="sm" className="bg-white dark:bg-[#131B2C] text-gray-900 dark:text-white border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:bg-white/5">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/20 shrink-0">
              <MousePointerClick className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-medium mb-1">Total Clicks</p>
              <h4 className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">{t.clicks.toLocaleString()}</h4>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center border border-purple-500/20 shrink-0">
              <Eye className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-medium mb-1">Total Impressions</p>
              <h4 className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">{t.impressions.toLocaleString()}</h4>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 flex items-center justify-center shrink-0">
              <Target className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-medium mb-1">Average CTR</p>
              <h4 className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">{(t.ctr * 100).toFixed(1)}%</h4>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center border border-orange-500/20 shrink-0">
              <BarChart3 className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-medium mb-1">Avg. Position</p>
              <h4 className="text-2xl font-bold text-gray-900 dark:text-white font-outfit">{t.position.toFixed(1)}</h4>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Queries Table */}
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-white/[0.04] flex items-center gap-2">
            <Search className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <h4 className="text-gray-900 dark:text-white font-semibold text-sm">Top Search Queries</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-white/[0.02] border-b border-gray-100 dark:border-white/[0.05]">
                <tr>
                  <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">Query</th>
                  <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8] text-right">Clicks</th>
                  <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8] text-right">Impr.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {data?.top_queries?.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:bg-white/[0.01]">
                    <td className="p-4 text-gray-900 dark:text-white font-medium">{row.keys[0]}</td>
                    <td className="p-4 text-gray-900 dark:text-white font-mono text-right">{row.clicks}</td>
                    <td className="p-4 text-gray-500 dark:text-[#94A3B8] font-mono text-right">{row.impressions}</td>
                  </tr>
                ))}
                {(!data?.top_queries || data.top_queries.length === 0) && (
                  <tr>
                    <td colSpan="3" className="p-8 text-center text-gray-500 dark:text-[#94A3B8]">No query data found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Top Pages Table */}
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-white/[0.04] flex items-center gap-2">
            <Search className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <h4 className="text-gray-900 dark:text-white font-semibold text-sm">Top Landing Pages</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-white/[0.02] border-b border-gray-100 dark:border-white/[0.05]">
                <tr>
                  <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">Page URL</th>
                  <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8] text-right">Clicks</th>
                  <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8] text-right">Impr.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {data?.top_pages?.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:bg-white/[0.01]">
                    <td className="p-4 text-emerald-600 dark:text-emerald-400 font-medium text-xs break-all truncate max-w-[200px]">{row.keys[0].replace('https://jamadhan.com', '')}</td>
                    <td className="p-4 text-gray-900 dark:text-white font-mono text-right">{row.clicks}</td>
                    <td className="p-4 text-gray-500 dark:text-[#94A3B8] font-mono text-right">{row.impressions}</td>
                  </tr>
                ))}
                {(!data?.top_pages || data.top_pages.length === 0) && (
                  <tr>
                    <td colSpan="3" className="p-8 text-center text-gray-500 dark:text-[#94A3B8]">No page data found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
